// premium-services-index: all services with metrics and clear CTAs
import React from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '@/components/navigation';
import { Footer } from '@/components/footer';
import { Button } from '@/components/ui/button';
import { ArrowRight, Clock, TrendingUp, Users } from 'lucide-react';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';
import { SEO } from '@/components/seo';

const services = [
  {
    name: "Website Development",
    slug: "website-development",
    pitch: "Fast, conversion-focused websites that grow your business",
    benefits: [
      "47% average conversion lift",
      "90+ Core Web Vitals scores",
      "2-4 week launch timeline"
    ],
    timeline: "2-4 weeks",
    icon: TrendingUp
  },
  {
    name: "UI/UX Design",
    slug: "ui-ux-design",
    pitch: "User experiences that feel intuitive and drive engagement",
    benefits: [
      "67% improvement in user retention",
      "42% faster task completion",
      "Research-backed design decisions"
    ],
    timeline: "3-5 weeks",
    icon: Users
  },
  {
    name: "Web App Development",
    slug: "web-app-development",
    pitch: "Scalable web applications built for growth and performance",
    benefits: [
      "99.9% uptime reliability",
      "50% faster load times",
      "Enterprise-grade security"
    ],
    timeline: "6-12 weeks",
    icon: TrendingUp
  },
  {
    name: "AI Integration",
    slug: "ai-integration",
    pitch: "Smart automation that reduces costs and improves outcomes",
    benefits: [
      "60% reduction in manual tasks",
      "35% improvement in accuracy",
      "ROI visible within 90 days"
    ],
    timeline: "4-8 weeks",
    icon: TrendingUp
  },
  {
    name: "Custom Software",
    slug: "custom-software",
    pitch: "Tailored solutions that solve your specific business challenges",
    benefits: [
      "100% fit to your workflow",
      "3x faster than off-the-shelf",
      "Full ownership and control"
    ],
    timeline: "8-16 weeks",
    icon: TrendingUp
  },
  {
    name: "Mobile App Development",
    slug: "mobile-app-development",
    pitch: "Native and cross-platform apps users love to use",
    benefits: [
      "4.8+ app store ratings",
      "85% user retention at 30 days",
      "iOS and Android deployment"
    ],
    timeline: "8-14 weeks",
    icon: TrendingUp
  },
  {
    name: "Ecommerce",
    slug: "ecommerce",
    pitch: "Online stores optimized for sales and customer experience",
    benefits: [
      "32% increase in conversion rates",
      "45% boost in average order value",
      "Seamless checkout experience"
    ],
    timeline: "4-8 weeks",
    icon: TrendingUp
  },
  {
    name: "SEO Services",
    slug: "seo-services",
    pitch: "Rank higher and attract more qualified organic traffic",
    benefits: [
      "150% increase in organic traffic",
      "Top 3 rankings for target keywords",
      "Technical SEO optimization"
    ],
    timeline: "3-6 months",
    icon: TrendingUp
  }
];

const Services = () => {
  const { elementRef, getRevealStyle } = useScrollReveal();

  const servicesSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": services.map((service, index) => ({
      "@type": "Service",
      "position": index + 1,
      "name": service.name,
      "description": service.pitch,
      "url": `https://www.affinityaim.com/services/${service.slug}`
    }))
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.affinityaim.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Services",
        "item": "https://www.affinityaim.com/services"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Our Services - Web Development, UI UX Design & AI Integration | AffinityAim"
        description="Explore our premium digital services: website development, UI/UX design, web apps, mobile apps, AI integration, and SEO. Proven results for growth-stage companies."
        canonical="https://www.affinityaim.com/services"
        keywords="web development services, UI UX design, web app development, mobile app development, AI integration, SEO services"
        schema={[servicesSchema, breadcrumbSchema]}
      />
      <Navigation />

      <main id="main-content">
        {/* Hero */}
        <section className="section-spacing bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-content mx-auto text-center">
              
              <div className="mb-16">
                <div className="micro-label mb-6">SERVICES</div>
                <h1 className="font-display font-bold text-foreground mb-6" style={{ fontSize: '56px', lineHeight: '1.1', letterSpacing: '-0.01em' }}>
                  We build digital products
                  <br />
                  <span className="bg-gradient-primary bg-clip-text text-transparent">
                    that deliver results
                  </span>
                </h1>
                <p className="text-body-lg text-muted-foreground max-w-paragraph mx-auto">
                  From websites to web apps, our senior team ships fast and measures what matters. Clear timelines, transparent process, proven outcomes.
                </p>
              </div>

            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="pb-20 bg-background">
          <div className="container mx-auto px-6">
            <div ref={elementRef} className="max-w-content-wide mx-auto">
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {services.map((service, index) => {
                  const IconComponent = service.icon;
                  return (
                    <Link 
                      key={service.slug}
                      to={`/services/${service.slug}`}
                      className="group hover-lift tap-target"
                      style={getRevealStyle(index)}
                      aria-label={`Learn more about ${service.name}`}
                    >
                      <div className="card-matte p-8 h-full">
                        
                        {/* Header */}
                        <div className="mb-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
                              <IconComponent className="w-5 h-5 text-primary" />
                            </div>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Clock className="w-4 h-4" />
                              {service.timeline}
                            </div>
                          </div>
                          
                          <h3 className="font-display font-semibold text-foreground mb-3 group-hover:text-primary transition-colors" style={{ fontSize: '20px', lineHeight: '1.3' }}>
                            {service.name}
                          </h3>
                          
                          <p className="text-muted-foreground leading-relaxed mb-6 tile-text">
                            {service.pitch}
                          </p>
                        </div>

                        {/* Benefits */}
                        <div className="mb-6">
                          <div className="space-y-3">
                            {service.benefits.map((benefit, idx) => (
                              <div key={idx} className="flex items-start gap-3">
                                <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                                <span className="text-sm text-muted-foreground">{benefit}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* CTA */}
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-muted-foreground">Learn more</span>
                          <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all duration-300" />
                        </div>

                      </div>
                    </Link>
                  );
                })}
              </div>

            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-background-secondary">
          <div className="container mx-auto px-6">
            <div className="max-w-content mx-auto text-center">
              
              <h2 className="font-display font-semibold text-foreground mb-6" style={{ fontSize: '40px', lineHeight: '1.1' }}>
                Not sure which service you need?
              </h2>
              <p className="text-muted-foreground mb-8 max-w-paragraph mx-auto">
                Book a 15-minute call and we'll help you choose the right approach for your goals and timeline.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button variant="default" size="lg" className="group bg-primary hover:bg-primary/90 text-primary-foreground tap-target">
                    Book a strategy call
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/portfolio">
                  <Button variant="ghost" size="lg" className="text-muted-foreground hover:text-foreground tap-target">
                    View our work
                  </Button>
                </Link>
              </div>

            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Services;